import React from "react";
import { Link, NavLink, useLocation, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { buildDossierPath, DOSSIER_MODES, isExpressDossierMode } from "../../data/dossierModes";

/** Botón con estado activo */
const Item = ({ to, children, end = false }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) =>
      `block px-4 py-3 rounded-2xl transition border text-sm font-medium 
       ${isActive
         ? "bg-emerald-600 text-white shadow-lg border-emerald-600"
         : "bg-white hover:bg-emerald-50 text-gray-800 border"}`
    }
  >
    {children}
  </NavLink>
);

/** Enlace con hash dentro del bloc (por si se usa en el futuro) */

const ModeLink = ({ to, active, children }) => (
  <Link
    to={to}
    className={`block rounded-xl border px-3 py-2 text-xs font-semibold transition ${
      active
        ? "border-emerald-600 bg-emerald-50 text-emerald-800 shadow-sm"
        : "border-gray-200 bg-white text-gray-700 hover:bg-emerald-50"
    }`}
  >
    {children}
  </Link>
);

const AnchorItem = ({ href, children }) => (
  <a
    href={href}
    className="block px-4 py-3 rounded-2xl transition border text-sm font-medium bg-white hover:bg-emerald-50 text-gray-800"
  >
    {children}
  </a>
);

export default function DossierSidebar({ mode = DOSSIER_MODES.STANDARD }) {
  const { t } = useTranslation();
  const { blocSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const base = "/dossier";
  const inBloc = /^\/dossier\/bloc\/[^/]+/.test(location.pathname);
  const blocBase = blocSlug ? `/dossier/bloc/${blocSlug}` : "";
  const isExpress = isExpressDossierMode(mode);
  const currentPath = location.pathname || base;

  return (
    <nav
      className="
        fixed left-6 top-1/2 -translate-y-1/2 z-40
        w-64 px-4 py-6 
        bg-white/90 backdrop-blur-md
        rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.12)] ring-1 ring-black/10
        flex flex-col gap-3
      "
      aria-label={t("ui.dossier.title", "Materials Didàctics")}
    >
      {/* Back + chip Dossier */}
      <div className="flex items-center gap-2 mb-2">
        <button
          onClick={() => navigate(-1)}
          className="px-3 py-1.5 rounded-xl border text-xs font-medium hover:bg-gray-50"
          title={t("ui.common.back", "Enrere")}
        >
          ← {t("ui.common.back", "Enrere")}
        </button>
        <NavLink
          to={buildDossierPath(base, mode)}
          className="px-3 py-1.5 rounded-xl border text-xs font-medium hover:bg-gray-50"
          title={t("ui.dossier.title", "Materials Didàctics")}
        >
          🗂 HOME
        </NavLink>
      </div>

      <div className="space-y-2 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-2">
        <div className="px-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-900/70">
          {t("ui.dossier.modeSelector.title", "Modalitat")}
        </div>
        <ModeLink
          to={buildDossierPath(currentPath, DOSSIER_MODES.STANDARD)}
          active={!isExpress}
        >
          {t("ui.dossier.modes.standard", "LivingSoils")}
        </ModeLink>
        <ModeLink
          to={buildDossierPath(currentPath, DOSSIER_MODES.EXPRESS)}
          active={isExpress}
        >
          {t("ui.dossier.modes.express", "LivingSoils Express")}
        </ModeLink>
      </div>

      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1">
        {t("ui.dossier.title", "Materials Didàctics")}
      </div>

      {/* 1) Overview */}
      {inBloc ? (
        <Item to={buildDossierPath(blocBase, mode)} end>
          {t("ui.dossier.sidebar.overview", "Vista general")}
        </Item>
      ) : (
        <Item to={buildDossierPath(base, mode)} end>
          {t("ui.dossier.sidebar.overview", "Vista general")}
        </Item>
      )}

      {/* ❌ Eliminado: Documents */}
      {/* ❌ Eliminado: Projecte */}

      {/* 2) Sessions (en bloc lo dejamos como ancla a #guia si quieres mantener ese comportamiento) */}
      {inBloc ? (
        <AnchorItem href={buildDossierPath(blocBase, mode, "#guia")}>
          {t("ui.dossier.sidebar.sessions", "Sessions")}
        </AnchorItem>
      ) : (
        <Item to={buildDossierPath(`${base}/sessions`, mode)}>
          {t("ui.dossier.sidebar.sessions", "Sessions")}
        </Item>
      )}
    </nav>
  );
}
